import { apiCall, isBackendConfigured } from './api-call'
import {
  PublicRenewalOffer,
  RenewalOfferResult,
  RespondToRenewalOfferInput,
} from '../_types/renewal-offer'

const RENEWAL_OFFER_GROUP = 'renewaloffer'
const UNREACHABLE_MESSAGE =
  'We could not reach QuickPass just now. Please try again in a moment.'

/**
 * Loads the offer behind a renewal offer link. Works signed out: the token in
 * the link is the only credential.
 *
 * @param {string} token - the token from the link.
 * @returns {Promise<RenewalOfferResult>} the offer, or why there is none.
 */
const getRenewalOffer = async (token: string): Promise<RenewalOfferResult> => {
  if (isBackendConfigured() === false) {
    return { offer: null, errorMessage: UNREACHABLE_MESSAGE }
  }

  try {
    const response = await apiCall({
      group: RENEWAL_OFFER_GROUP,
      command: 'get',
      data: { token },
    })

    if (response.code !== 200) {
      return { offer: null, errorMessage: response.message }
    }

    return {
      offer: JSON.parse(response.data) as PublicRenewalOffer,
      errorMessage: '',
    }
  } catch (error) {
    console.warn('[renewal-offer] get failed', error)
    return { offer: null, errorMessage: UNREACHABLE_MESSAGE }
  }
}

/**
 * Accepts (with a typed signature) or declines the offer behind a link.
 *
 * @param {RespondToRenewalOfferInput} input - the token and the answer.
 * @returns {Promise<RenewalOfferResult>} the updated offer, or why it failed.
 */
const respondToRenewalOffer = async (
  input: RespondToRenewalOfferInput,
): Promise<RenewalOfferResult> => {
  if (isBackendConfigured() === false) {
    return { offer: null, errorMessage: UNREACHABLE_MESSAGE }
  }

  try {
    const response = await apiCall({
      group: RENEWAL_OFFER_GROUP,
      command: 'respond',
      data: {
        token: input.token,
        decision: input.decision,
        signerName: input.signerName,
        declineReason: input.declineReason,
      },
    })

    if (response.code !== 200) {
      return { offer: null, errorMessage: response.message }
    }

    return {
      offer: JSON.parse(response.data) as PublicRenewalOffer,
      errorMessage: '',
    }
  } catch (error) {
    console.warn('[renewal-offer] respond failed', error)
    return { offer: null, errorMessage: UNREACHABLE_MESSAGE }
  }
}

export { getRenewalOffer, respondToRenewalOffer }
